import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react"
import GridContainer from "@/components/common/GridContainer"
import { getMapKey } from "@/utils/utils"
import TimeSpan from "@/components/Archive/TimeSpan"
import styled from "styled-components"
import Timeline from "@/components/Archive/Timeline"
import useCustomContext from "@/hooks/useCustomContext"
import useScrollContext from "@/hooks/useScrollContext"
import { BREAKPOINTS } from "@/styles/themeSettings"
import { useIsMobile, useIsTablet } from "@/hooks/useDevice"

const MainContainer = styled(GridContainer)`
	row-gap: 0;
	margin: 0;

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		margin: 0;
		padding-left: var(--grid-margin-l);
		padding-right: var(--grid-margin-r);
	}
`

const Container = styled(GridContainer)`
	background-color: ${({ theme }) => theme.colors.richBlack};
	row-gap: 0;
	position: relative;
	margin: 0;
	display: flex;
	flex-direction: column;
	//z-index: 3;

	// @media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.TABLET]}px){
	//     margin-left: calc(-2 * var(--grid-gap,30px)) !important;
	// }
	//
	// @media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
	//     margin-left: unset;
	// }

	padding-left: var(--grid-margin-l);
	padding-right: var(--grid-margin-r);

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.TABLET]}px) {
		display: initial;
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		padding: unset;
		margin-right: calc(-1 * var(--paddingX, 30px));
	}
`

const INIT_CONTEXT = {
	timeSpans: [],
	current: "",
	setCurrent: () => {},
}

const ArchiveContext = createContext(INIT_CONTEXT)

const Archive = ({ data }) => {
	const leftSideText = data?.leftSideText || ""
	const [MainRef, setMainRef] = useState()
	const TimeSpanRefs = useRef([])
	const [currentTs, setCurrentTS] = useState("")
	const scroll = useScrollContext()
	const isMobile = useIsMobile()
	const isTablet = useIsTablet()

	const [fullSpans, mappedSpans] = useMemo(() => {
		const source = data?.timeSpans || []
		const timeSpans = source.map((timeSpan) => {
			const { id, date } = timeSpan
			return {
				id,
				date,
			}
		})
		return [source, timeSpans]
	}, [data])
	const context = useMemo(
		() => ({
			timeSpans: fullSpans,
			current: currentTs,
			setCurrent: setCurrentTS,
		}),
		[fullSpans, currentTs],
	)

	const handleTimeSpanClick = useCallback(
		(span) => {
			const element = TimeSpanRefs.current.find((item) => item.id === span.id)
			if (!element || !element?.ref) return
			const { top } = element.ref.getBoundingClientRect()
			const scrollProps = scroll.props()
			let offset = isMobile ? 100 : isTablet ? 100 : 1
			if (isMobile || isTablet) {
				offset *= -1
			} else {
				offset = top > 0 ? offset : -offset
			}

			const target = top + scrollProps.scroll + offset
			scroll.scrollTo({
				top: target,
				behavior: "smooth",
			})
		},
		[scroll, isMobile, isTablet],
	)

	const handleSkipButtonClick = useCallback(() => {
		if (!MainRef) return
		const { bottom } = MainRef.getBoundingClientRect()
		const scrollProps = scroll.props()
		const target = bottom + scrollProps.scroll
		scroll.scrollTo({
			top: target,
			behavior: "smooth",
		})
	}, [MainRef, scroll])

	useEffect(() => {
		const curr = mappedSpans?.[0]?.date || INIT_CONTEXT.current
		setCurrentTS(curr)
	}, [mappedSpans])

	// console.log(dates, timeSpans);
	return (
		<MainContainer ref={setMainRef}>
			<ArchiveContext.Provider value={context}>
				<Timeline
					leftSideText={leftSideText}
					data={mappedSpans}
					onTimeSpanClick={handleTimeSpanClick}
					onSkipButtonClick={handleSkipButtonClick}
					mainRef={MainRef}
				/>
				<Container className={`col-mobile-8 col-desktop-15 no-margin-l`}>
					{fullSpans.map((timeSpan, index) => (
						<TimeSpan
							key={getMapKey(timeSpan, index)}
							ref={(ref) => (TimeSpanRefs.current[index] = { id: timeSpan.id, ref })}
							data={timeSpan}
						/>
					))}
				</Container>
			</ArchiveContext.Provider>
		</MainContainer>
	)
}

export function useArchiveContext(selector = undefined) {
	return useCustomContext(ArchiveContext, selector)
}

export default Archive
